import { useCallback, useState } from "react";
import { type ChangeEventLike, restoreFromLocalStorage } from "@/util/localStorageConfig.ts";

const defaultTwitchConfig = {
  twitchUsername: "",
  twitchAccessToken: "",
  twitchRefreshToken: "",
  twitchBroadcasterID: "",
  twitchCampaignID: "",
  twitchCampaignGoal: "0.00",
  twitchCampaignName: "",
};

export type TwitchConfig = typeof defaultTwitchConfig;

/** React-state hook for the shared Twitch config (localStorage-backed). */
export default function useTwitchConfig() {
  const [config, setConfig] = useState<TwitchConfig>(() =>
    restoreFromLocalStorage(defaultTwitchConfig),
  );

  const setField = useCallback(<K extends keyof TwitchConfig>(key: K, value: TwitchConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    localStorage.setItem(key, String(value));
  }, []);

  const setFromEvent = useCallback((e: ChangeEventLike) => {
    const { name, value } = e.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
    localStorage.setItem(name, value);
  }, []);

  const clearField = useCallback((key: keyof TwitchConfig) => {
    setConfig((prev) => ({ ...prev, [key]: defaultTwitchConfig[key] }));
    localStorage.removeItem(key);
  }, []);

  return { config, setField, setFromEvent, clearField };
}
